import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft } from "lucide-react";
import ProviderSearch from "@/components/ProviderSearch";

export default function BrowseProvidersPage() {
  const navigate = useNavigate();
  const [sessionId] = useState(`browse_${Date.now()}`);
  const [selectedCareType, setSelectedCareType] = useState("");

  const careTypes = [
    { value: "Home Care", label: "Home Care", description: "Help with daily activities" },
    { value: "Home Health", label: "Home Health", description: "Skilled medical care at home" },
    { value: "Palliative Care", label: "Palliative Care", description: "Live better with a serious illness — support at any stage, alongside treatment" },
    { value: "Hospice Care", label: "Hospice Care", description: "End-of-life comfort care" },
    { value: "Senior Advisor", label: "Senior Advisor", description: "Guidance navigating care options" },
    { value: "Adult Day Care", label: "Adult Day Care", description: "Daytime programs & respite" },
    { value: "Home Physicians", label: "Home Physicians", description: "House-call doctors" },
    { value: "Community Partners", label: "Community Partners", description: "Nonprofits, faith groups, Meals on Wheels & more" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-home-button"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              type="button"
              data-testid="browse-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3 group"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-mark-512.png"
                alt="Tyvena"
                className="w-10 h-10 rounded-full object-cover shadow-md group-hover:shadow-lg transition-shadow"
              />
              <h1 className="text-xl font-heading font-bold text-primary group-hover:text-primary-dark transition-colors">Browse Care Providers</h1>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedCareType ? (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
                What type of care are you looking for?
              </h2>
              <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
                Select the care type that best matches your needs, or{" "}
                <button
                  onClick={() => navigate("/chat")}
                  className="text-primary font-semibold hover:underline"
                >
                  talk to Asher
                </button>{" "}
                if you're not sure.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {careTypes.map((type) => (
                <button
                  key={type.value}
                  data-testid={`select-${type.value.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSelectedCareType(type.value)}
                  className="bg-white rounded-xl border-2 border-[#E2E8F0] shadow-sm hover:shadow-md hover:border-primary transition-all duration-300 p-8 text-left border-l-4 border-l-secondary"
                >
                  <h3 className="text-2xl font-heading font-semibold text-text-primary mb-3">
                    {type.label}
                  </h3>
                  <p className="text-base text-text-secondary font-body">
                    {type.description}
                  </p>
                </button>
              ))}
            </div>

            <div className="text-center">
              <Button
                data-testid="view-all-providers-btn"
                onClick={() => setSelectedCareType("All")}
                variant="outline"
                className="min-h-[48px] px-8 border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-bold font-body"
              >
                View All Providers
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <Button
              data-testid="change-care-type-btn"
              onClick={() => setSelectedCareType("")}
              variant="ghost"
              className="text-primary hover:bg-primary/5 font-body font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Change Care Type
            </Button>
            
            <ProviderSearch 
              careType={selectedCareType === "All" ? null : selectedCareType}
              sessionId={sessionId}
            />
          </div>
        )}
      </main>
    </div>
  );
}
